import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Pencil, Plus, Trash2 } from 'lucide-react';

import { adotarArea, excluirArea, type Area, type Configuracao } from '@/api/secretario';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { AreaForm } from './AreaForm';
import { AreaPicker, type EscolhaArea } from './AreaPicker';
import { useAdvogadoEmEdicao } from './advogadoEmEdicao';

interface AreasManagerProps {
  config: Configuracao;
  /**
   * No perfil do advogado: só a lista das áreas que atendem, debaixo dos botões que as
   * marcam (o catálogo já está neles), e "Outra área" para a que não está no catálogo.
   */
  compacto?: boolean;
}

export function AreasManager({ config, compacto = false }: AreasManagerProps) {
  const queryClient = useQueryClient();
  const advogado = useAdvogadoEmEdicao();
  const { showSuccess, showError } = useNotification();
  // 'novo' cria; um id edita. A área editada é derivada do config (sempre fresca
  // após invalidação — os editores de doc/pergunta salvam e invalidam o config).
  const [form, setForm] = useState<'novo' | { editId: string } | null>(null);
  // Área recém-criada: faz ponte até o config refetchar (o find abaixo a substitui
  // assim que ela aparece no config), evitando "piscar" para o modo "nova".
  const [criada, setCriada] = useState<Area | null>(null);
  const [areaParaExcluir, setAreaParaExcluir] = useState<Area | null>(null);
  // Identidade do modal aberto. NÃO deriva de 'nova'/id: ao criar a área o form
  // passa de "nova" a "editando" e, com a chave mudando, o React o remontava —
  // o diálogo piscava fechado e voltava na primeira aba.
  const [formKey, setFormKey] = useState(0);
  const abrirForm = (alvo: 'novo' | { editId: string }) => {
    setFormKey((k) => k + 1);
    setForm(alvo);
  };

  const areaEditando =
    form && form !== 'novo' ? (config.areas.find((a) => a.id === form.editId) ?? criada) : null;

  const fecharForm = () => {
    setForm(null);
    setCriada(null);
  };

  // Só mostra o Dialog se for criação OU se a área editada ainda existe — se ela
  // sumir (excluída em outra aba), o form desmonta em vez de recriá-la ao salvar.
  const mostrarForm = form === 'novo' || (form !== null && areaEditando !== null);

  const prontidaoPorArea = new Map(config.prontidao.por_area.map((a) => [a.area_id, a]));

  const excluir = useMutation({
    mutationFn: (id: string) => excluirArea(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });
      showSuccess('Área removida.');
      setAreaParaExcluir(null);
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  const adotar = useMutation({
    mutationFn: (chave: string) => (advogado ? adotarArea(chave, advogado.id) : adotarArea(chave)),
    onSuccess: (area) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });
      // Abre a edição já com os documentos/perguntas do catálogo (a área criada faz
      // ponte até o config refetchar — ver areaEditando).
      setCriada(area);
      abrirForm({ editId: area.id });
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  const handleEscolher = (escolha: EscolhaArea) => {
    if (escolha.tipo === 'nova') abrirForm('novo');
    else if (escolha.tipo === 'editar') abrirForm({ editId: escolha.areaId });
    else adotar.mutate(escolha.chave);
  };

  const areas = compacto ? config.areas.filter((a) => a.ativo || !a.catalogo_chave) : config.areas;

  return (
    <div className="space-y-4">
      {!compacto && (
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">Áreas de atuação</h2>
          <p className="text-sm text-muted-foreground">
            O Secretário identifica a área na triagem. Cada área pode ter seus documentos, perguntas, modelos e
            honorários próprios.
          </p>
        </div>
        <div className="shrink-0">
          <AreaPicker config={config} disabled={adotar.isPending} onEscolher={handleEscolher} />
        </div>
      </div>
      )}

      {compacto && areas.length === 0 ? null : areas.length === 0 ? (
        <EmptyState
          title="Nenhuma área cadastrada"
          description="Escolha uma área de atuação acima para começar."
        />
      ) : (
        <ul className="space-y-2">
          {areas.map((area) => {
            const pa = prontidaoPorArea.get(area.id);
            const pendencias = pa?.pendencias.map((p) => p.descricao).join(' · ');
            return (
              <li
                key={area.id}
                className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="min-w-0 break-words font-medium">{area.nome}</span>
                    {!area.ativo ? (
                      <Badge variant="secondary">Inativa</Badge>
                    ) : pa?.pronta ? (
                      <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
                        <CheckCircle2 size={14} /> Fecha contrato
                      </span>
                    ) : (
                      // Não é erro: a área atende e entrega o caso ao advogado. O
                      // que falta para ela também fechar contrato vai no title.
                      <span className="text-xs font-medium text-muted-foreground" title={pendencias}>
                        Pré-atendimento
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {area.documentos_requeridos.length} documentos · {area.perguntas.length} perguntas
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => abrirForm({ editId: area.id })}
                    aria-label={`Editar ${area.nome}`}
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setAreaParaExcluir(area)}
                    aria-label={`Remover ${area.nome}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {compacto && (
        <Button type="button" variant="ghost" className="-ml-3 min-h-11" onClick={() => abrirForm('novo')}>
          <Plus className="mr-1.5 h-4 w-4" />
          Outra área
        </Button>
      )}

      {mostrarForm && form !== null && (
        <AreaForm
          key={formKey}
          open
          onOpenChange={(aberto) => !aberto && fecharForm()}
          area={areaEditando}
          honorariosGerais={config.honorarios}
          onCreated={(area) => {
            setCriada(area);
            setForm({ editId: area.id });
          }}
        />
      )}

      <ConfirmDialog
        open={areaParaExcluir !== null}
        title="Remover área"
        message={`Remover a área "${areaParaExcluir?.nome}"? Os documentos e perguntas dela serão apagados.`}
        confirmLabel="Remover"
        confirmColor="error"
        loading={excluir.isPending}
        onConfirm={() => areaParaExcluir && excluir.mutate(areaParaExcluir.id)}
        onCancel={() => setAreaParaExcluir(null)}
      />
    </div>
  );
}
